<template>
    <label :class="classes">
        <input :id="`field-${name}-input`"
               :name="name"
               :aria-labelledby="`field-${name}-label`"
               :value="value"
               @input="updateValue"
               :placeholder="placeholder"
               class="field-input"
               :type="type"
        >
        <span :id="`field-${name}-label`"
              class="field-label"
              aria-hidden="true"
              v-if="label">
            {{ label }}
        </span>
    </label>
</template>

<script>
  export default {
    name: 'BaseField',

    props: {
      type: {
        type:    String,
        default: 'text',
      },

      value: {
        type:    null,
        default: '',
      },

      name: {
        type:     String,
        required: true,
      },

      label: {
        type:     String,
        required: false,
      },

      placeholder: {
        type:     String,
        required: false,
      },

      required: {
        type:    Boolean,
        default: false,
      },

      inverseColors: {
        type:    Boolean,
        default: false,
      },

      autofill: {
        type:    Boolean,
        default: false,
      },
    },

    computed: {
      baseClasses () {
        return {
          'field':                true,
          'field-required':       this.required,
          'field-inverse-colors': this.inverseColors,
        };
      },
    },

    methods: {
      updateValue ( event ) {
        this.$emit('input', event.target.value);
      },

      focus () {
        if ( !this.$refs.input ) {
          return;
        }

        this.$refs.input.focus();
      },
    },
  };
</script>

<style>
    .field {
        position:          relative;
        display:           flex;
        min-width:         15rem;

        --input-color:     var(--color-black);
        --label-color:     var(--color-gray-dark);
        --inactive-color:  var(--color-gray);
        --highlight-color: var(--color-primary);
        --error-color:     var(--color-red);
    }

    .field.field-inverse-colors {
        --input-color:     var(--color-white);
        --label-color:     var(--color-gray);
        --inactive-color:  var(--color-white);
        --highlight-color: var(--color-white);
        --error-color:     var(--color-red);
    }

    .field::after {
        content:             '';
        position:            absolute;
        top:                 0;
        left:                0;
        width:               100%;
        height:              100%;
        background:          none;
        pointer-events:      none;
        animation:           reveal;
        animation-delay:     0.125s;
        animation-duration:  0.5s;
        animation-fill-mode: forwards;
    }

    .field.field-type-basic {
        flex-direction: column;
        cursor:         text;
    }

    .field + .field {
        margin-top: 1.5rem;
    }

    .field .field-label {
        user-select: none;
        color:       var(--label-color);
        font-size:   1rem;
        font-family: inherit;
    }

    .field.field-type-basic .field-input {
        font-size:   1rem;
        font-family: inherit;
        color:       var(--input-color);
    }

    .field.field-type-basic .field-label {
        position:       absolute;
        order:          1;
        bottom:         0;
        padding:        0.25rem 0;
        border-bottom:  1px solid transparent;
        pointer-events: none;
        transition:     transform 0.125s, color 0.125s, font-size 0.125s;
    }

    .field.field-type-basic .field-input,
    .field.field-type-select .field-input {
        order:              2;
        width:              100%;
        -webkit-appearance: none;
        background:         none;
        outline:            none;
        padding:            0.5rem 0;
        border:             none;
        border-bottom:      1px solid var(--inactive-color);
        transition:         border 0.125s, box-shadow 0.125s;
    }

    .field.field-type-basic .field-input:not(:placeholder-shown) {
        border-bottom: 1px solid var(--inactive-color);
        box-shadow:    0 1px 0 0 var(--inactive-color);
    }

    .field.field-type-basic:focus-within .field-input,
    .field.field-type-select:focus-within .field-input {
        border-bottom: 1px solid var(--highlight-color);
        box-shadow:    0 1px 0 0 var(--highlight-color);
    }

    .field.field-type-basic .field-input:not(:placeholder-shown) + .field-label,
    .field.field-type-basic:focus-within .field-label {
        font-size:      0.75rem;
        transform:      translateY(-1.75rem);
        pointer-events: auto;
    }

    .field.field-type-basic:focus-within .field-label,
    .field.field-type-basic:focus-within .field-input[required] + .field-label::after {
        color: var(--highlight-color);
    }

    .field.field-type-basic .field-input::-webkit-input-placeholder {
        color:       transparent;
        user-select: none;
    }

    .field .field-input::selection {
        color:      #f2f2f2;
        background: var(--highlight-color);
    }

    .field.field-type-basic .field-input[required] + .field-label::after {
        content:     '*';
        margin-left: 0.125rem;
        color:       var(--error-color);
        transition:  color 0.125s;
        font-size:   inherit;
        font-weight: bold;
    }

    .field.field-type-checkbox {
        justify-content: space-between;
    }

    .field.field-type-select {
        padding-top: 0.5rem;
    }

    .field.field-type-select .field-label {
        position:       absolute;
        order:          1;
        top:            0;
        padding:        0.25rem 0;
        border-bottom:  1px solid transparent;
        font-size:      0.75rem;
        transform:      translateY(-0.75rem);
        pointer-events: auto;
        transition:     transform 0.125s, color 0.125s, font-size 0.125s;
    }

    .field.field-type-select option {
        padding: 0.5rem;
    }

    /*
    .field {
        display: flex;
    }

    .field .field-label {
        order: 1;
    }

    .field .field-input {
        order:              2;
        -webkit-appearance: none;
        font-size:          1rem;
        font-family:        inherit;
        padding:            0.25rem 0;
        border:             0;
        border-bottom:      1px solid #d0d0d0;
        background:         none;
    }

    .field .field-input:hover {
        box-shadow: 0 1px 0 0 #d0d0d0;
    }

    .field .field-input:focus {
        outline: none;
    }

     */
</style>
